import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { HelperText } from "../styels";

const buildSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderWidth: 1.5,
    borderColor: hasError
      ? "#d32f2f"
      : state.isFocused
      ? "#3fc4fd"
      : "#dce1e8",
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(63, 196, 253, 0.12)"
      : "none",
    "&:hover": {
      borderColor: hasError ? "#d32f2f" : "#3fc4fd",
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "2px 12px",
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#aab0bc",
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 10,
    overflow: "hidden",
    zIndex: 30,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#eef9ff" : "#fff",
    color: "#1a1a2e",
    cursor: "pointer",
  }),
});

const ProductSelect = ({
  value = "",
  onChange,
  onResolve,
  companyId = "",
  date,
  billingInfo = {},
  disabled = false,
  error = "",
  api,
  onError,
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchValue(searchValue.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchValue]);

  useEffect(() => {
    let active = true;

    const loadProducts = async () => {
      setLoading(true);
      try {
        const [productResponse, priceResponse] = await Promise.all([
          api.getProducts({
            skip: 0,
            limit: 50,
            searchkey: debouncedSearchValue,
            isActive: true,
          }),
          api.getProductPrices({
            skip: 0,
            limit: 200,
            activeOn: date || new Date().toISOString(),
          }),
        ]);

        if (!active) return;

        setProducts(productResponse.status === 200 ? productResponse.data?.data || [] : []);
        setPrices(priceResponse.status === 200 ? priceResponse.data?.data || [] : []);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      active = false;
    };
  }, [api, debouncedSearchValue, date]);

  const priceMap = useMemo(() => {
    const map = {};
    prices.forEach((item) => {
      const key = item.productId?._id || item.productId;
      if (!key || map[key]) return;
      map[key] = item;
    });
    return map;
  }, [prices]);

  const options = useMemo(
    () =>
      products.map((product) => {
        const activePrice = priceMap[product._id];
        const priceLabel = activePrice
          ? `${activePrice.price} ${activePrice.currency}`
          : t("priceUnavailable") || "Price unavailable";

        return {
          value: product._id,
          label: product.name,
          description: product.description,
          priceLabel,
        };
      }),
    [priceMap, products, t]
  );

  const selectedOption =
    options.find((option) => option.value === value) || null;

  const handleSelection = async (option) => {
    const nextValue = option?.value || "";
    onChange(nextValue);

    if (!nextValue || !companyId || !onResolve) {
      return;
    }

    setResolving(true);
    try {
      const response = await api.resolveProductPrice({
        companyId,
        productId: nextValue,
        date: date || new Date().toISOString(),
        billingInfo,
      });

      if (response.status === 200) {
        onResolve(response.data);
      } else if (onError) {
        onError(response);
      }
    } finally {
      setResolving(false);
    }
  };

  return (
    <div>
      <Select
        isClearable
        isSearchable
        isDisabled={disabled || loading || resolving}
        value={selectedOption}
        options={options}
        styles={buildSelectStyles(Boolean(error))}
        placeholder={t("selectProduct") || "Select product"}
        noOptionsMessage={() => t("noProductsFound") || "No products found"}
        onInputChange={(inputValue, meta) => {
          if (meta.action === "input-change") {
            setSearchValue(inputValue);
          }
        }}
        onChange={handleSelection}
        formatOptionLabel={(option) => (
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 700 }}>{option.label}</div>
              {option.description ? (
                <div style={{ fontSize: 12, color: "#838894" }}>
                  {option.description}
                </div>
              ) : null}
            </div>
            <div style={{ fontSize: 12, color: "#1381c5", fontWeight: 700 }}>
              {option.priceLabel}
            </div>
          </div>
        )}
      />
      {error ? <HelperText $error>{error}</HelperText> : null}
    </div>
  );
};

export default ProductSelect;
